import { inject, Injectable } from "@angular/core";
import { FormControl } from "@angular/forms";
import { AppStateService } from "@modules/core/services/app-state.service";
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, firstValueFrom, map, merge, of, Subject, take } from "rxjs";
import { SearchState, TrafficDeviationSearchStore } from "../stores/traffic-deviation-search.store";
import { Call, RouteInfo } from "@components/traffic-deviations/interfaces/traffic-deviation-form";
import { TranslateService } from "@modules/core/services";
import { LineSearchResult, LineSearchStop } from "../components/search-stop/interfaces/line-search-result";
import { MapApiService } from "@modules/core/services/map-api/map-api.service";
import { TrafficDeviationService } from "@components/traffic-deviations/services/traffic-deviation.service";
import { Router } from "@angular/router";
import { StopSearchResult, StopPlace, StopArea, EffectedRoutesOnStop } from "../components/search-stop/interfaces";
import { TrafficDeviationAccessService } from "@modules/core/services/traffic-deviation-access.service";

type SearchQueryType = 'stop' | 'route';

@Injectable({
  providedIn: 'root'
})
export class TrafficDeviationSearchService {
  private readonly _router = inject(Router);
  private readonly _appStateService = inject(AppStateService);
  private readonly _translateService = inject(TranslateService);
  private readonly _mapApiService = inject(MapApiService);
  private readonly _trafficDeviationService = inject(TrafficDeviationService);
  private readonly _searchStore = inject(TrafficDeviationSearchStore);
  private readonly _trafficDeviationAccessService = inject(TrafficDeviationAccessService);
  
  private _lastStopSearchQuery = '';
  
  public readonly choosenStops$ = new BehaviorSubject<StopSearchResult[]>([]);
  public readonly choosenLines$ = new BehaviorSubject<LineSearchResult[]>([]);
  public readonly selectedLineInBlade$ = new BehaviorSubject<LineSearchResult | undefined>(undefined);
  public readonly focusSearchField$ = new Subject<void>();
  public readonly stopSearchResultUpdates$ = new Subject<StopSearchResult[]>();
  public readonly retractedStopPoint: StopPlace = {
    hastusRegion: '',
    placeLatitude: Number.NaN,
    placeLongitude: Number.NaN,
    description: this._translateService.instant('Bus stop retracted'),
    platformCode: '',
    placeId: 'retracted',
    parentId: '',
    type: 'StopPlace',
    searchType: 'StopSearch'
  };
  public readonly existingTrafficDeviationLoaded$ = 
    this._trafficDeviationService.existingTrafficDeviationLoaded$.asObservable();

  private readonly _selectedGarages$ = this._appStateService.getSelectedGaragesState();
  private _searchSessionId = 0;
  private _currentSessionId = 0;
  private _pendingCacheQuery = '';
  private _pendingCacheResults: StopSearchResult[] | null = null;

  public async openStopsInBlade(selectedLine: LineSearchResult, showArea = true): Promise<void> {
    const lineInOpenBlade = this.selectedLineInBlade$.getValue();
    if (
      lineInOpenBlade?.routeId === selectedLine.routeId 
      && lineInOpenBlade.firstStopDestinationSign === selectedLine.firstStopDestinationSign
    ) {
      this.closeStopsInBlade();
      return;
    }

    if (!selectedLine.stopPlaces?.length) {
      const dates = this._getDateTimeIntervalInForm();
      selectedLine.stopPlaces = await this._mapApiService.getStopsByRouteId(
        selectedLine.routeId, 
        selectedLine.hastusRegion,
        dates.startDateTime, 
        dates.endDateTime,
        selectedLine.direction,
        selectedLine.templateTripId
      );
    }

    this.selectedLineInBlade$.next(selectedLine);
    let path = ['selected-line'];
    if (showArea) path.push('stop-areas');
    if (!showArea) path.push('stop-places');
    this._router.navigate(['', { outlets: { outlet1: path, 'outlet-right0': null, 'outlet-right1': null, 'outlet-right2': null }}]);
  }

  public closeStopsInBlade(): void {
    if (!this.selectedLineInBlade$.getValue()) return;
    this.selectedLineInBlade$.next(undefined);
    this._router.navigate(['', { outlets: { outlet1: null }}]);
  }

  public setSearchState(partialState: Partial<SearchState>): void {
    const state = {...this._searchStore.stateSnapshot, ...partialState};
    this._searchStore.setSearchState(state);
  }

  public mapSelectedStopsToChoosenStops(selectedStops: StopSearchResult[]): StopSearchResult[] {
    const stopAreas: StopArea[] = [];
    const stopPlaces: StopPlace[] = [];

    selectedStops.forEach((stop) => {
      if (stop.type === 'StopArea') {
        stopAreas.push({
          ...structuredClone(stop),
          stopPlaces: []
        });
      } else {
        stopPlaces.push(this._ensureStopPointHasFallbackResult(structuredClone(stop)));
      }
    });

    stopAreas.forEach((stopArea) => {
      const matchingStopPlaces = stopPlaces.filter((stopPoint) => {
        return stopArea.placeId === stopPoint.parentId
          && stopArea.hastusRegion === stopPoint.hastusRegion;
      });

      stopArea.stopPlaces = matchingStopPlaces.map((stopPoint) => {
        return this._ensureStopPointHasFallbackResult(structuredClone(stopPoint));
      });
    });

    const choosenStops: StopSearchResult[] = stopAreas.length ? [...stopAreas] : [];
    stopPlaces.forEach((stopPoint) => {
      const nestedInArea = stopAreas.some((stopArea) => {
        return stopArea.stopPlaces?.some((areaStopPoint) => {
          return areaStopPoint.placeId === stopPoint.placeId
            && areaStopPoint.hastusRegion === stopPoint.hastusRegion;
        });
      });

      if (!nestedInArea) {
        stopPoint.active = true;
        choosenStops.push(stopPoint);
      }
    });

    return choosenStops;
  }
  
  public postQuery(
    search: FormControl<string | null | undefined>
  ) {
    const affected = this._trafficDeviationService.trafficDeviationForm.controls.affected;
    const startTime$ = merge(
      affected.controls.startDate.valueChanges,
      affected.controls.startTime.valueChanges
    );
    const endTime$ = merge(
      affected.controls.endDate.valueChanges,
      affected.controls.endTime.valueChanges
    );

    return merge(
        search.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged((p, c) => {
            if (this._classifySearchQuery(c ?? undefined) === 'stop' && this._classifySearchQuery(p ?? undefined) === 'stop') {
              return (p ?? '').toLowerCase() === (c ?? '').toLowerCase();
            }
            return p === c;
          }),
          map(a => [a, 1])
        ),
        this._selectedGarages$.pipe(map(c => [c, 3])),
        startTime$.pipe(map(d => [d, 4])),
        endTime$.pipe(map(e => [e, 5]))
    ).pipe(
        map(([current, index]) => {
          // Clear cache when garages change (index 3) or dates/time change (indices 4 and 5)
          // Different garages may return different stops, so cache must be invalidated
          if (index === 3 || index === 4 || index === 5) {
            this._searchStore.clearPlatformRoutesCache();
            this._lastStopSearchQuery = '';
            this.setCachedQuery();
            // Clear pending cache and increment session to invalidate any in-flight operations
            // This prevents background operations from old form/dates from caching results
            this._pendingCacheQuery = '';
            this._pendingCacheResults = null;
            this._searchSessionId++;
            this._currentSessionId = this._searchSessionId;
          }
          
          // any trigger other than search
          if (index !== 1) {
            return search.value;
          }
          return <string>current;
        })
    );
  }

  public async getStopPlacesQuery(query: string): Promise<StopSearchResult[]> {
    const dates = this._getDateTimeIntervalInForm();
    const regions = new Set(
      (await firstValueFrom(this._selectedGarages$))?.map(garage => garage.hastusRegionCode)
    );
    return this._mapApiService.postStops(
      query, [...regions], dates.startDateTime, dates.endDateTime
    );
  }

  public updateSelectedStopSearches(stop: StopSearchResult): void {
    this._searchStore.getSelectedStopSearches().pipe(take(1)).subscribe((selectedStopSearches) => {
      const stopIndex = selectedStopSearches.findIndex((s) => this._isSameStopSelection(s, stop));
      if (stopIndex !== -1) {
        selectedStopSearches = [...selectedStopSearches];
        selectedStopSearches.splice(stopIndex, 1, stop);
        this._searchStore.updateSearchState({ selectedStopSearches });
      }
    });
  }
    
  public getCurrentOrSelectedGaragesHastusRegions(): string[] {
    const regions: string[] = [];
    combineLatest([this._searchStore.getSelectedHastusRegion(), this._selectedGarages$]).pipe(
      take(1)
    ).subscribe(([selectedGarage, selectedGarages]) => {
      selectedGarages = selectedGarages?.filter((g) => {
        return this._trafficDeviationAccessService.isTrafficDeviationRegion(g.hastusRegionCode);
      });

      if (selectedGarage) {
        return regions.push(selectedGarage);
      } else {
        selectedGarages?.forEach((garage) => {
          const find = regions.find((g) => g === garage.hastusRegionCode)
          if (!find) regions.push(garage.hastusRegionCode);
        });
      }
    });
    return regions;
  }
  
  public convertRouteInfoToSelectedLinesAndStops(
    routeInfos: RouteInfo[], hastusRegion: string
  ): void {
    if (!routeInfos?.length || !hastusRegion) return;

    const isStopCentric = routeInfos.some((r) => r.stopCentric);
    const isJourneyPattern = this._isJourneyPatternDeviation();
    const uniqueStopPoints = this._collectUniqueStopPointsFromRouteInfos(
      routeInfos,
      hastusRegion,
      isJourneyPattern
    );
    const uniqueStopAreas = this._buildUniqueStopAreasFromStopPoints(
      uniqueStopPoints,
      hastusRegion,
      isStopCentric
    );

    this._searchStore.updateSearchState({selectedStopSearches: [...uniqueStopAreas, ...uniqueStopPoints]});
  }

  /**
   * 
   * @param stop the stop which to find effectedRoutes for
   * @param defaultActive sets the active flag on each route returned
   * @param lineSearchResult include the lineSearchResult to find which line to return as active as default
   * @returns lines that stops at the specific stop between the specified time interval
   */
  public async getRoutesForStop(
    stop: StopSearchResult | LineSearchStop, 
    defaultActive = true,
    lineSearchResult?: LineSearchResult
  ): Promise<EffectedRoutesOnStop[]> {
    if (stop.effectedRoutes?.length) {
      if (lineSearchResult) {
        const activeRoute = stop.effectedRoutes.find((er) => {
          return er.routeId === lineSearchResult.routeId 
            && lineSearchResult.firstStopDestinationSign === er.firstStopDestinationSign;
        });
        if (activeRoute) activeRoute.active = true;
      }
      return stop.effectedRoutes;
    }

    const dates = this._getDateTimeIntervalInForm();
    const effectedRoutes: EffectedRoutesOnStop[] = (await this._mapApiService.getRoutesByPlaceId(
      stop.placeId, stop.hastusRegion, dates.startDateTime, dates.endDateTime
    )).map((routes) => ({...routes, active: defaultActive}));

    const activeRoute = effectedRoutes.find((r) => {
      return r.routeId === lineSearchResult?.routeId 
        && r.firstStopDestinationSign === lineSearchResult.firstStopDestinationSign;
    });
    if (activeRoute) activeRoute.active = true;

    return effectedRoutes;
  }

  public async toggleStopSearchResult(selectedStop: StopSearchResult): Promise<void> {
    selectedStop.selected = !selectedStop.selected;
    const selectedStopSearches = [...this._searchStore.stateSnapshot.selectedStopSearches];
    const stopIndex = selectedStopSearches.findIndex((s) => this._isSameStopSelection(s, selectedStop));
    if (stopIndex !== -1) {
      const stop = {...selectedStopSearches[stopIndex]};
      stop.selected = !selectedStop.selected;
      selectedStopSearches.splice(stopIndex, 1, stop);
    }

    if (!selectedStop.selected) {
      const stopPlaces = this.selectedLineInBlade$.getValue()?.stopPlaces;
      const stopPlace = stopPlaces?.find((s) => s.stopAreaId === selectedStop.placeId);
      if (stopPlace) stopPlace.selected = false;
    }

    await this._addOrRemoveStopWasAdded(selectedStop, selectedStopSearches);

    if (selectedStop.type === 'StopArea') {
      if (selectedStop.selected) {
        const selectedStopPoints = this._searchStore.stateSnapshot.cachedQueryResults?.filter((x) => {
          return (
            x.type === 'StopPlace' 
            && x.parentId === selectedStop.placeId
            && x.hastusRegion === selectedStop.hastusRegion
            && !selectedStopSearches.some((s) => this._isSameStopSelection(s, x))
          );
        })?.map((s) => {
          s.selected = selectedStop.selected;
          return s;
        });
  
        if (selectedStopPoints) {
          await Promise.all(selectedStopPoints.map(async (stopPoint) => {
            await this._addOrRemoveStopWasAdded(stopPoint, selectedStopSearches);
          }));
        }
      } 
      if (!selectedStop.selected) {
        const stops = selectedStopSearches.filter((x) => {
          return x.type === 'StopPlace'
            && x.parentId === selectedStop.placeId
            && x.hastusRegion === selectedStop.hastusRegion;
        });
        await Promise.all(stops.map(async (stop) => {
          await this._addOrRemoveStopWasAdded(stop, selectedStopSearches);
        }));
      }
    }

    this._searchStore.updateSearchState({selectedStopSearches});
  }

  public resetSearchState(): void {
    this._searchStore.resetSearchState();
    this._searchStore.clearPlatformRoutesCache();
    this._lastStopSearchQuery = '';
    this.selectedLineInBlade$.next(undefined);
    this.choosenLines$.next([]);
    this.choosenStops$.next([]);
    // Clear pending cache and increment session to invalidate any in-flight operations
    // This prevents background operations from old form from caching results
    this._pendingCacheQuery = '';
    this._pendingCacheResults = null;
    this._searchSessionId++;
    this._currentSessionId = this._searchSessionId;
  }

  public postStopsOrRoutesRequest(
    query: string
  ): Promise<StopSearchResult[] | LineSearchResult[]> {
    const queryType = this._classifySearchQuery(query);
    if (queryType === 'stop' && this._isStopPointQuery(query)) {
      return firstValueFrom(of([]));
    }

    const hastusRegions = this.getCurrentOrSelectedGaragesHastusRegions();
    const dates = this._getDateTimeIntervalInForm();
    if (queryType === 'stop') {
      return this._mapApiService.postStops(
        query, hastusRegions, dates.startDateTime, dates.endDateTime
      );
    }

    return this._mapApiService.postRoutesByLine(
      query, hastusRegions, dates.startDateTime, dates.endDateTime
    );
  }

  /**
   * Helper method to save/clear original query and query result.
   * @param query the search string
   * @param result result from query
   */
  public setCachedQuery(
    query: string | number = '', 
    result?: StopSearchResult[]
  ): void {
    const cachedQueryResults = structuredClone(result);
    const cachedQueryString = String(query);
    cachedQueryResults?.forEach((x) => x.selected = undefined);
    this._searchStore.updateSearchState({cachedQueryResults, cachedQueryString});
  }

  public async toggleLineSearchResult(selectedLine: LineSearchResult): Promise<void> {
    const deviationType = await firstValueFrom(
      this._trafficDeviationService.selectedDeviationType$
    );

    if (deviationType === 'ChangeOfJourneyPattern') {
      await this.openStopsInBlade(selectedLine);
      this.setHastusRegion(selectedLine.hastusRegion);
    }
    if (deviationType === 'DeviationInformation') {
      if (selectedLine.selected) this.removeLineFromChoosenLines(selectedLine); 
      else {
        this.choosenLines$.next([
          ...this.choosenLines$.getValue(), 
          selectedLine
        ]);
        this.setHastusRegion(selectedLine.hastusRegion);
      }      
    }
  }

  public removeLineFromChoosenLines(line: LineSearchResult): void {
    const selectedLines = this.choosenLines$.getValue();
    const index = selectedLines.findIndex((l) => {
      return l.routeId === line.routeId 
          && l.firstStopDestinationSign === line.firstStopDestinationSign;
    });
    if (index !== -1) {
      selectedLines.splice(index, 1);
      this.choosenLines$.next(selectedLines);  
      const openBlade = this.selectedLineInBlade$.getValue();
      if (
        openBlade?.routeId === line.routeId 
        && openBlade.firstStopDestinationSign === line.firstStopDestinationSign
      ) {
        this.closeStopsInBlade();
      }
    }

    const routeInfo = this._trafficDeviationService.trafficDeviationForm.controls.affected.controls.routeInfo;
    const routeInfoIndex = routeInfo.value.findIndex((r) => {
      return r.routeId === line.routeId 
          && r.firstStopDestinationSign === line.firstStopDestinationSign;
    });
    if (routeInfoIndex !== -1) {
      routeInfo.removeAt(routeInfoIndex);
    }
  }

  public setHastusRegion(selectedHastusRegion = ''): void {
    this._searchStore.getSelectedHastusRegion().pipe(take(1)).subscribe((hastusRegion) => {
      if (hastusRegion) {
        const choosenLines = this.choosenLines$.getValue().length;
        this._searchStore.getSelectedStopSearches().pipe(take(1)).subscribe((selectedStopSearches) => {
          if (!choosenLines && !selectedStopSearches.length) {
            this._searchStore.updateSearchState({selectedHastusRegion: ''});
          }
        });
      } else {
        this._searchStore.updateSearchState({selectedHastusRegion});
      }
    });
  }

  public async handleQueryResponse(
    response: StopSearchResult[] | LineSearchResult[], query = '', fromCache = false
  ): Promise<StopSearchResult[] | LineSearchResult[]> {
    if (this._classifySearchQuery(query) === 'stop') {
      return await this.handleStopSearch(query, <StopSearchResult[]>response, fromCache);
    }

    return this.handleLineSearch(<LineSearchResult[]>response);
  }
  
  public async handleStopSearch(
    query: string, stops: StopSearchResult[], fromCache = false
  ): Promise<StopSearchResult[]> {
    this._compareStopResultsWithSelectedStops(stops);

    if (!stops.length) {
      return await this._handleStopPointQuery(query);
    }

    // If all results are platforms (StopPlace) from cache, handle them specially
    // _toStopAreaResults filters out platforms, so we need to handle them separately
    if (fromCache && stops.every(stop => stop.type === 'StopPlace')) {
      // Check if any platforms need routes fetched
      const platformsNeedingRoutes = stops.filter((stop) => 
        !stop.effectedRoutes?.length && !(stop as any).__skipRouteFetch
      );
      
      if (platformsNeedingRoutes.length > 0) {
        await this._fetchRoutesForAllPlatformsInParallel(platformsNeedingRoutes);
      }
      
      return stops;
    }

    // Increment session ID for each new search (not for cached results)
    if (!fromCache) {
      this._searchSessionId++;
      this._currentSessionId = this._searchSessionId;
      // Store pending cache info but don't finalize cache yet - wait for routes to complete
      this._pendingCacheQuery = query;
      this._pendingCacheResults = stops;
      // Set temporary cache immediately so filtering works while ByPlaceId loads
      // This will be replaced with the final cache (with routes) when ByPlaceId completes
      this.setCachedQuery(query, stops);
    }

    this._lastStopSearchQuery = query.toLowerCase();
    return await this._toStopAreaResults(query, stops, fromCache, this._currentSessionId);
  }

  public canFilterFromCache(query: string): boolean {
    // Stop point queries (ending with space) need special handling to show platforms
    // They should bypass cache and go through _handleStopPointQuery
    if (this._isStopPointQuery(query)) {
      return false;
    }

    const queryLower = query.toLowerCase();
    const cachedResults = this._searchStore.stateSnapshot.cachedQueryResults;
    const cachedQueryString = this._searchStore.stateSnapshot.cachedQueryString?.toLowerCase();
    
    if (!cachedResults?.length || !cachedQueryString) {
      return false;
    }
    
    // Can use cache when the new query is a refinement of the cached query
    // or when it matches the cached query exactly
    // Example: cache is "ra", query is "rap" → can filter
    // Example: cache is "ra", query is "ra" → can reuse
    // Example: cache is "ra", query is "r" → cannot use (would need broader results)
    if (queryLower.startsWith(cachedQueryString) || queryLower === cachedQueryString) {
      return true;
    }
    
    return false;
  }

  public filterCachedResults(query: string): StopSearchResult[] {
    const cached = this._searchStore.stateSnapshot.cachedQueryResults;
    if (!cached?.length) return [];
    
    const queryLower = query.toLowerCase();
    return cached.filter((result) => {
      // Filter both StopAreas and StopPlaces (platforms)
      if (result.type === 'StopArea') {
        return result.description.toLowerCase().includes(queryLower);
      }
      if (result.type === 'StopPlace') {
        // For platforms, match against both description and platform code
        const searchableText = `${result.description.toLowerCase()} ${result.platformCode.toLowerCase()}`;
        return searchableText.includes(queryLower);
      }
      return false;
    }).map((result) => {
      // Deep clone to preserve state and prevent accidental mutations
      const cloned = structuredClone(result);
      // Preserve the route loading state from the cached result
      if (result.type === 'StopArea') {
        const stateArea = cloned as StopArea;
        stateArea.stopSearchLoader = (cloned as StopArea).stopSearchLoader ?? false;
        // Only mark as skip if it already has routes - otherwise we need to fetch them
        if (cloned.effectedRoutes?.length) {
          (stateArea as any).__skipRouteFetch = true;
        }
      } else if (result.type === 'StopPlace') {
        const statePlatform = cloned as StopPlace;
        statePlatform.stopSearchLoader = (cloned as StopPlace).stopSearchLoader ?? false;
        // Only mark as skip if it already has routes - otherwise we need to fetch them
        if (cloned.effectedRoutes?.length) {
          (statePlatform as any).__skipRouteFetch = true;
        }
      }
      return cloned;
    });
  }

  public handleLineSearch(lineSearchResults: LineSearchResult[]): LineSearchResult[] {
    if (!lineSearchResults?.length) return [];

    const selectedLineSearches = this.choosenLines$.getValue();
    if (selectedLineSearches.length) {
      for (const line of selectedLineSearches) {
        const searchResultsLine = lineSearchResults.find((l) => {
          return l.routeId === line.routeId 
            && l.firstStopDestinationSign === line.firstStopDestinationSign;
        });
        if (searchResultsLine) searchResultsLine.selected = true;
      }
    }

    return lineSearchResults;
  }

  public handleQueryBeforeRequest(query: string): string | undefined {
    this.closeStopsInBlade();
    const queryType = this._classifySearchQuery(query);
    if (queryType === 'stop') {
      if (query.length < 2) return;
    }

    const cachedQuery = this._searchStore.stateSnapshot.cachedQueryResults;
    if (cachedQuery?.length) {
      // if the latest query contains letters but the _cachedQueryResults
      // is not of type StopSearchResult, clear the _cachedQueryResults.
      if (
        cachedQuery[0].searchType !== 'StopSearch' && queryType === 'stop'
        || cachedQuery[0].searchType === 'StopSearch' && queryType === 'route'
      ) {
        this.setCachedQuery();
        // Clear pending cache and increment session to invalidate any in-flight operations
        this._pendingCacheQuery = '';
        this._pendingCacheResults = null;
        this._searchSessionId++;
        this._currentSessionId = this._searchSessionId;
      }
    }
    return query;
  }

  private _classifySearchQuery(query: string | undefined): SearchQueryType {
    const trimmed = query?.trim();
    if (!trimmed?.length) return 'stop';

    const routePatterns = [
      /^\d+[a-z]?$/i,
      /^[a-z]\d{1,4}[a-z]?$/i,
      /^[a-z]{1,3}\d+[a-z]?$/i,
      /^(line|linje|route)\s*\d+[a-z]?$/i,
    ];

    if (routePatterns.some((pattern) => pattern.test(trimmed))) {
      return 'route';
    }

    if (/\d/.test(trimmed) && trimmed.length <= 6 && !trimmed.includes(' ')) {
      return 'route';
    }

    return 'stop';
  }

  private _isStopPointQuery(query: string): boolean {
    return query.endsWith(' ');
  }

  private async _handleStopPointQuery(query: string): Promise<StopSearchResult[]> {
    if (!this._isStopPointQuery(query)) {
      return [];
    }

    let cachedQueryResults = this._searchStore.stateSnapshot.cachedQueryResults;
    
    // Check if trimmed query matches a stop area name exactly
    const trimmedQuery = query.endsWith(' ') ? query.slice(0, -1) : query.slice(0, -2);
    
    const exactMatchStopAreas = cachedQueryResults?.filter((result) => {
      return result.type === 'StopArea' 
        && result.description.toLowerCase() === trimmedQuery.toLowerCase();
    }) || [];
    
    // Check if we have stop points for the exact match areas in cache
    const cachedStopPoints = cachedQueryResults?.filter((cachedResult) => {
      if (cachedResult.type !== 'StopPlace') return false;
      
      return exactMatchStopAreas.some((area) => {
        return cachedResult.parentId === area.placeId
          && cachedResult.hastusRegion === area.hastusRegion;
      });
    }) || [];
    
    // If we have exact match areas but no stop points, make a fresh API call
    if (exactMatchStopAreas.length > 0 && cachedStopPoints.length === 0) {
      cachedQueryResults = await this._loadCachedStopResultsForStopPointQuery(query);
      
      // Re-find stop points after fresh API call
      const stopPointResults = cachedQueryResults.filter((cachedResult) => {
        if (cachedResult.type !== 'StopPlace') return false;
        
        return exactMatchStopAreas.some((area) => {
          return cachedResult.parentId === area.placeId
            && cachedResult.hastusRegion === area.hastusRegion;
        });
      });
      
      // Fetch routes for all platforms in parallel
      if (stopPointResults.length > 0) {
        await this._fetchRoutesForAllPlatformsInParallel(stopPointResults);
      }

      return stopPointResults;
    }
    
    // If no cached results at all, load from API
    if (!cachedQueryResults?.length) {
      cachedQueryResults = await this._loadCachedStopResultsForStopPointQuery(query);
    }

    if (!cachedQueryResults?.length) {
      return [];
    }

    // If no exact match, return stop areas (not individual stop points)
    if (exactMatchStopAreas.length === 0) {
      return await this._toStopAreaResults(trimmedQuery, cachedQueryResults);
    }

    // Get all stop points that belong to any of the exact match stop areas (across all regions)
    const stopPointResults = cachedStopPoints;

    // Fetch routes for all platforms in parallel
    if (stopPointResults.length > 0) {
      await this._fetchRoutesForAllPlatformsInParallel(stopPointResults);
    }

    return stopPointResults;
  }

  private async _fetchRoutesForAllPlatformsInParallel(platforms: StopSearchResult[]): Promise<void> {
    const dates = this._getDateTimeIntervalInForm();
    
    // Fetch routes for all platforms in parallel
    const routeFetchPromises = platforms.map(async (platform) => {
      // Check if routes are already cached (using composite key: placeId + hastusRegion)
      const cachedRoutes = this._searchStore.getPlatformRoutes(platform.placeId, platform.hastusRegion);
      if (cachedRoutes) {
        platform.effectedRoutes = cachedRoutes;
        return;
      }

      // Fetch routes from API
      const routes = await this._mapApiService.getRoutesByPlaceId(
        platform.placeId,
        platform.hastusRegion,
        dates.startDateTime,
        dates.endDateTime
      );

      const effectedRoutes = routes.map((r) => ({ ...r, active: true }));
      platform.effectedRoutes = effectedRoutes;

      // Cache the routes (using composite key: placeId + hastusRegion)
      this._searchStore.setPlatformRoutes(platform.placeId, platform.hastusRegion, effectedRoutes);
    });

    await Promise.all(routeFetchPromises);
  }

  private async _loadCachedStopResultsForStopPointQuery(query: string): Promise<StopSearchResult[]> {
    const hastusRegions = this.getCurrentOrSelectedGaragesHastusRegions();
    const dates = this._getDateTimeIntervalInForm();
    const newQuery = query.endsWith(' ') ? query.slice(0, -1) : query.slice(0, -2);

    const cachedQueryResults = await this._mapApiService.postStops(
      newQuery, hastusRegions, dates.startDateTime, dates.endDateTime
    );

    this._compareStopResultsWithSelectedStops(cachedQueryResults);
    
    // Only cache if this query is broader than (or equal to) the existing cached query
    // This prevents overwriting a broad cache (e.g., "sl") with a narrow one (e.g., "slalomvägen")
    const existingCachedQuery = this._searchStore.stateSnapshot.cachedQueryString;
    if (cachedQueryResults.length && (!existingCachedQuery || newQuery.length <= existingCachedQuery.length)) {
      this.setCachedQuery(newQuery, cachedQueryResults);
    }

    return cachedQueryResults;
  }

  private _getStopPointResultsFromCachedQuery(
    query: string,
    cachedQueryResults: StopSearchResult[]
  ): StopSearchResult[] {
    return cachedQueryResults.filter((cachedResult) => {
      if (cachedResult.type !== 'StopPlace') return false;

      const searchableStopName = `${cachedResult.description.toLowerCase()} ${cachedResult.platformCode.toLowerCase()}`;
      return searchableStopName.startsWith(query.toLowerCase());
    });
  }

  private async _toStopAreaResults(
    query: string,
    stops: StopSearchResult[],
    fromCache = false,
    sessionId: number = this._currentSessionId
  ): Promise<StopSearchResult[]> {
    const stopAreaResults = stops.filter((stop): stop is StopArea => stop.type === 'StopArea');
    
    // Check if query exactly matches any stop areas (handles multiple regions like Slussen NAV + Slussen TYR)
    const exactMatchStopAreas = stopAreaResults.filter((area) => {
      return area.description.toLowerCase() === query.toLowerCase();
    });

    // Return results immediately with loading state
    const areasToReturn = exactMatchStopAreas.length > 0 ? exactMatchStopAreas : stopAreaResults;
    
    // Separate areas that need routes fetched from those that already have them or are loading
    // Skip fetching for cached results (marked with __skipRouteFetch)
    const areasNeedingRoutes = areasToReturn.filter((area) => 
      !area.effectedRoutes?.length && !area.stopSearchLoader && !(area as any).__skipRouteFetch
    );
    const areasWithRoutesOrLoading = areasToReturn.filter((area) => area.effectedRoutes?.length || area.stopSearchLoader);
    
    // Set loader only for areas that need fetching
    areasNeedingRoutes.forEach((area) => {
      area.stopSearchLoader = true;
    });

    // Fetch routes only for areas that don't have them and aren't already loading
    if (areasNeedingRoutes.length > 0) {
      // Track completion count to know when to cache
      let completedCount = 0;
      const totalCount = areasNeedingRoutes.length;
      
      // Fetch routes individually and emit after each completes
      areasNeedingRoutes.forEach((area) => {
        this.getRoutesForStop(area).then((routes) => {
          // Ignore if this search session is no longer active
          if (sessionId !== this._currentSessionId) {
            return;
          }
          
          area.effectedRoutes = routes;
          area.stopSearchLoader = false;
          completedCount++;
          
          // Emit immediately after this specific stop's routes are fetched
          this.stopSearchResultUpdates$.next(areasToReturn);
          
          // After the last one completes, cache the results with routes
          if (completedCount === totalCount) {
            // Clean up cache marker flag
            areasToReturn.forEach((area) => {
              delete (area as any).__skipRouteFetch;
            });
            // Now cache with routes included
            if (!fromCache && this._pendingCacheQuery && this._pendingCacheResults) {
              this.setCachedQuery(this._pendingCacheQuery, this._pendingCacheResults);
              this._pendingCacheQuery = '';
              this._pendingCacheResults = null;
            }
          }
        }).catch(() => {
          // Ignore if this search session is no longer active
          if (sessionId !== this._currentSessionId) {
            return;
          }
          
          area.stopSearchLoader = false;
          completedCount++;
          
          // Emit even on error to remove loader
          this.stopSearchResultUpdates$.next(areasToReturn);
          
          // After the last one completes (even with error), cache if needed
          if (completedCount === totalCount) {
            // Clean up cache marker flag
            areasToReturn.forEach((area) => {
              delete (area as any).__skipRouteFetch;
            });
            // Now cache with routes included (or without if all failed)
            if (!fromCache && this._pendingCacheQuery && this._pendingCacheResults) {
              this.setCachedQuery(this._pendingCacheQuery, this._pendingCacheResults);
              this._pendingCacheQuery = '';
              this._pendingCacheResults = null;
            }
          }
        });
      });
    } else if (areasWithRoutesOrLoading.length > 0) {
      // If all areas already have routes, cache immediately
      if (!fromCache && this._pendingCacheQuery && this._pendingCacheResults) {
        this.setCachedQuery(this._pendingCacheQuery, this._pendingCacheResults);
        this._pendingCacheQuery = '';
        this._pendingCacheResults = null;
      }
      this.stopSearchResultUpdates$.next(areasToReturn);
    }

    // Clean up cache marker flag before returnin
    areasToReturn.forEach((area) => {
      delete (area as any).__skipRouteFetch;
    });
    return areasToReturn;
  }

  private _getDateTimeIntervalInForm(): { startDateTime: string, endDateTime: string } {
    const affected = this._trafficDeviationService.trafficDeviationForm.controls.affected;
    let startDateTime = '', endDateTime = '';          
    startDateTime = this._trafficDeviationService.formatAndOrCombineDateAndTime(
      affected.controls.startDate.value, affected.controls.startTime.value
    ) ?? '';
    endDateTime = this._trafficDeviationService.formatAndOrCombineDateAndTime(
      affected.controls.endDate.value, affected.controls.endTime.value
    ) ?? '';

    return { startDateTime, endDateTime };
  }
  
  private _compareStopResultsWithSelectedStops(stops: StopSearchResult[]) {
    this._searchStore.stateSnapshot.selectedStopSearches?.forEach((selectedStop) => {
      const stop = stops.find((s) => this._isSameStopSelection(s, selectedStop));
      if (stop) {
        stop.selected = true;
        stop.effectedRoutes = selectedStop.effectedRoutes;
      }
    });
  }

  /**
   * Add or remove stop from array, not immutable.
   * @param stop 
   * @param selectedStops 
   */
  private async _addOrRemoveStopWasAdded(
    stop: StopSearchResult, selectedStops: StopSearchResult[]
  ): Promise<void> {
    const selectedIndex = selectedStops.findIndex((s) => this._isSameStopSelection(s, stop));
    if (selectedIndex !== -1) {      
      selectedStops.splice(selectedIndex, 1);
    } else if (stop.selected) {
      stop.effectedRoutes = await this.getRoutesForStop(stop);
      if (stop.type === 'StopPlace' && stop.effectedRoutes.length) stop.active = true;
      selectedStops.push(stop);
    }
  }

  private _isSameStopSelection(
    left: Pick<StopSearchResult, 'placeId' | 'hastusRegion' | 'type'>,
    right: Pick<StopSearchResult, 'placeId' | 'hastusRegion' | 'type'>
  ): boolean {
    return left.placeId === right.placeId
      && left.hastusRegion === right.hastusRegion
      && left.type === right.type;
  }

  private _ensureStopPointHasFallbackResult(stop: StopPlace): StopPlace {
    if (stop.stopSearchResults?.length) return stop;

    stop.stopSearchResults = [{
      ...this.retractedStopPoint,
      hastusRegion: stop.hastusRegion
    }];

    return stop;
  }

  private _createStopPoint(
    call: Call, 
    routeInfo: RouteInfo,
    hastusRegion: string
  ): StopPlace {
    let effectedRoutes: EffectedRoutesOnStop[] = []; 
    if (call.effectedRoutes?.length) {
      effectedRoutes = call.effectedRoutes.map((er) => ({ 
        firstStopDestinationSign: er.firstStopDestinationSign,
        routeId: routeInfo.routeId,
        routeName: routeInfo.routeName,
        routePublicName: routeInfo.routePublicName,
        direction: routeInfo.direction ?? 0,
        hastusRegion: hastusRegion,
        active: !routeInfo.stopCentric,
        templateTripId: ''
      }));
    } else {
      // NOTE: The routeId "|| '11'" part is a legacy compatibility fallback for old deviations in prod.
      // Remove when all traffic deviations are created after 2025-04-03.
      effectedRoutes = [{
        routeId: routeInfo.routeId || '11',
        active: !routeInfo.stopCentric,
        direction: routeInfo.direction ?? 0,
        firstStopDestinationSign: routeInfo.firstStopDestinationSign,
        routeName: routeInfo.routeName,
        routePublicName: routeInfo.routePublicName,
        hastusRegion: hastusRegion,
        templateTripId: ''
      }];
    }

    return {
      hastusRegion: hastusRegion,
      placeLatitude: call.placeLatitude,
      placeLongitude: call.placeLongitude,
      description: call.name,
      platformCode: call.platformCode,
      placeId: call.placeId,
      parentId: call.parentId,
      type: 'StopPlace',
      searchType: 'StopSearch',
      active: true,
      effectedRoutes: effectedRoutes, 
      replaceName: call.replaceName,
      replacePlaceId: call.replacePlaceId
    };
  }

  private _addUniqueEffectiveRoutes(
    stopArea: StopArea, 
    stopPoint: StopPlace
  ): void {
    const stopPointRoutes = stopPoint.effectedRoutes ?? [];
    const stopAreaRoutes = stopArea.effectedRoutes ?? [];
    stopArea.effectedRoutes = stopAreaRoutes;

    stopPointRoutes.forEach((effectedRoute) => {
      const route = stopAreaRoutes.find((r) => {
        return r.routeId === effectedRoute.routeId
          && r.firstStopDestinationSign === effectedRoute.firstStopDestinationSign
          && r.direction === effectedRoute.direction;
      });
      if (!route) stopAreaRoutes.push({ ...effectedRoute });
    });
  }

  private _isJourneyPatternDeviation(): boolean {
    let isJourneyPattern = false;

    this._trafficDeviationService.deviationType$.pipe(
      take(1)
    ).subscribe((types) => {
      isJourneyPattern = types.journeyPattern;
    });

    return isJourneyPattern;
  }

  private _collectUniqueStopPointsFromRouteInfos(
    routeInfos: RouteInfo[],
    hastusRegion: string,
    isJourneyPattern: boolean
  ): StopPlace[] {
    const uniqueStopPoints: StopPlace[] = [];

    routeInfos.forEach((routeInfo) => {
      routeInfo.calls.forEach((call) => {
        const stopPoint = uniqueStopPoints.find((u) => u.placeId === call.placeId);
        if (!stopPoint) {
          if (isJourneyPattern && call.replaceName === undefined) {
            call.replaceName = this._translateService.instant('Bus stop retracted');
            call.replacePlaceId = 'retracted';
          }

          const createdStopPoint = this._createStopPoint(call, routeInfo, hastusRegion);
          // If parentId is empty, use the description as a synthetic parentId for grouping
          if (!createdStopPoint.parentId) {
            createdStopPoint.parentId = createdStopPoint.description;
          }
          uniqueStopPoints.push(createdStopPoint);
          return;
        }

        stopPoint.effectedRoutes ??= [];
        stopPoint.effectedRoutes.push({
          firstStopDestinationSign: routeInfo.firstStopDestinationSign,
          routeId: routeInfo.routeId,
          routeName: routeInfo.routeName,
          routePublicName: routeInfo.routePublicName,
          direction: routeInfo.direction ?? 0,
          hastusRegion: hastusRegion,
          active: !routeInfo.stopCentric,
          templateTripId: ''
        });
      });
    });

    return uniqueStopPoints;
  }

  private _buildUniqueStopAreasFromStopPoints(
    uniqueStopPoints: StopPlace[],
    hastusRegion: string,
    isStopCentric: boolean
  ): StopArea[] {
    const uniqueStopAreas: StopArea[] = [];

    uniqueStopPoints.forEach((stopPoint) => {
      // If parentId exists, use it as the area identifier
      // If not, use the description (base stop name) to group stops
      const areaIdentifier = stopPoint.parentId || stopPoint.description;

      const stopArea = uniqueStopAreas.find((s) => {
        return s.placeId === areaIdentifier;
      });

      if (!stopArea) {
        uniqueStopAreas.push({
          hastusRegion: hastusRegion,
          description: stopPoint.description,
          placeId: areaIdentifier,
          type: 'StopArea',
          searchType: 'StopSearch',
          effectedRoutes: [...(stopPoint.effectedRoutes ?? [])],
          stopPlaces: [],
          selected: false,
          stopCentric: isStopCentric
        });
        return;
      }

      this._addUniqueEffectiveRoutes(stopArea, stopPoint);
    });

    return uniqueStopAreas;
  }
}
